/**
 * Клиент API бэклога и аутентификации.
 *
 * Flow: логин уходит в backend-api (/api/auth/login), полученный токен
 *       используется для запросов к backlog-api (/api/backlog/*). Оба пути
 *       проксируются nginx тем же origin, поэтому CORS не возникает.
 *
 * Зависимости: только fetch браузера.
 *
 * Связанные файлы:
 *   - src/App.jsx — потребитель;
 *   - docs/interface/openapi.json — контракт, который здесь используется.
 */

const TOKEN_KEY = 'kaznacheistvo.backlog.token'

// Профиль в localStorage больше НЕ хранится.
//
// Прежде вход отдавал поле user, и оно складывалось сюда. С карточкой
// BL-2026-10-08-a315 профиль перестал быть строкой таблицы (в него входят
// выданные роли разделы), и единственным его источником стал /api/me —
// backend-api убрал user из ответа входа совсем. Сохранённая копия означала бы
// второй, отстающий профиль: роль правят в любой момент, а localStorage помнит
// её с прошлого входа.
//
// Ключ остаётся объявленным, чтобы вычистить значение, записанное прежней
// версией интерфейса: она складывала сюда undefined, и JSON.parse на старте
// падал, унося с собой весь экран.
const LEGACY_USER_KEY = 'kaznacheistvo.backlog.user'

// Вычистка — при загрузке модуля, а НЕ внутри setSession: при восстановлении
// сессии по сохранённому токену setSession не зовётся вовсе, и мусор дожил бы
// до следующего входа или выхода.
localStorage.removeItem(LEGACY_USER_KEY)

/** Сохранённый access-токен либо null. */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

/** Сохраняет токен сессии; null очищает её. */
export function setSession(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

/**
 * Выполняет запрос к API.
 *
 * @param {string} path — путь, начиная с /api
 * @param {object} options — { method, body }
 * @returns {Promise<object>} разобранный JSON
 * @throws {Error} с текстом из поля detail; для 409 в err.missingStages лежит
 *         список незакрытых обязательных этапов — UI показывает именно их,
 *         а не общее «нельзя закрыть».
 */
async function request(path, { method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' }
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (response.status === 401) setSession(null)

  const text = await response.text()
  const data = text ? JSON.parse(text) : {}
  if (!response.ok) {
    const err = new Error(data.detail || `Ошибка ${response.status}`)
    err.status = response.status
    err.missingStages = data.missing_stages
    throw err
  }
  return data
}

/**
 * Вход: возвращает { access_token, expires_at }.
 *
 * Профиля в ответе НЕТ — за ним идёт отдельный getMe(). Так решено на стороне
 * backend-api (карточка BL-2026-10-08-a315): источник профиля один.
 */
export function login(username, password) {
  return request('/api/auth/login', { method: 'POST', body: { username, password } })
}

/**
 * Профиль текущего пользователя — ЕДИНСТВЕННЫЙ его источник.
 *
 * Зовётся и сразу после входа, и при восстановлении сессии по сохранённому
 * токену: второго пути узнать роль нет.
 *
 * @returns {Promise<{username: string, role: string, full_name?: string,
 *                    sections: Array<{code: string, title: string}>}>}
 */
export function getMe() {
  return request('/api/me')
}

/** Список карточек с фильтрами. */
export function listItems({ status, type, assignee, query, archived } = {}) {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (type) params.set('type', type)
  if (assignee) params.set('assignee', assignee)
  if (query) params.set('q', query)
  if (archived) params.set('include_archived', 'true')
  const qs = params.toString()
  return request(`/api/backlog${qs ? `?${qs}` : ''}`)
}

/** Карточка целиком: этапы, комментарии, связи. */
export function getItem(id) {
  return request(`/api/backlog/${id}`)
}

/** Создание карточки. */
export function createItem(payload) {
  return request('/api/backlog', { method: 'POST', body: payload })
}

/** Смена статуса. */
export function setStatus(id, status, extra = {}) {
  return request(`/api/backlog/${id}/status`, { method: 'POST', body: { status, ...extra } })
}

/** Взятие в работу. */
export function takeItem(id, location = 'server', branch = '') {
  return request(`/api/backlog/${id}/take`, { method: 'POST', body: { location, branch } })
}

/** Комментарий. */
export function addComment(id, body) {
  return request(`/api/backlog/${id}/comment`, { method: 'POST', body: { body } })
}

/** Статус этапа. */
export function setStage(id, key, status, kind = 'phase') {
  return request(`/api/backlog/${id}/stages/${key}`, { method: 'PUT', body: { status, kind } })
}

/** Чтение session-context. */
export function getContext(id) {
  return request(`/api/backlog/${id}/session-context`)
}

/** Запись session-context. */
export function putContext(id, content) {
  return request(`/api/backlog/${id}/session-context`, {
    method: 'PUT',
    body: { content, format: 'markdown' },
  })
}

/** Архивирование карточки. */
export function archiveItem(id) {
  return request(`/api/backlog/${id}/archive`, { method: 'POST', body: {} })
}

/** Этапы проекта с прогрессом и составом карточек. */
export function listMilestones() {
  return request('/api/milestones')
}

/**
 * Перестановка карточки в очереди исполнения.
 *
 * @param id - переставляемая карточка
 * @param after - карточка, после которой встать (null — в начало)
 * @param before - карточка, перед которой встать (null — в конец)
 */
export function moveItem(id, after, before) {
  return request(`/api/backlog/${id}/move`, {
    method: 'POST',
    body: { after: after || '', before: before || '' },
  })
}

/** Общее состояние проекта. */
export function getProjectStatus() {
  return request('/api/project-status')
}

/** Запись общего состояния проекта. */
export function saveProjectStatus(body) {
  return request('/api/project-status', { method: 'PUT', body: { body } })
}

/** Прогоны проверок по карточке. */
export function listTestRuns(id) {
  return request(`/api/backlog/${id}/test-runs`)
}

/** Запись прогона проверок. */
export function addTestRun(id, kind, result, summary) {
  return request(`/api/backlog/${id}/test-runs`, {
    method: 'POST',
    body: { kind, result, summary },
  })
}

/** Опубликованный граф архитектуры вместе с историей. */
export function getArchitecture() {
  return request('/api/architecture')
}
